"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, Flame, Droplets, TriangleAlert as AlertTriangle, MapPin, PhoneCall, ArrowRight, ShieldAlert, Clock, Sparkles, CircleQuestionMark as HelpCircle, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
const services = [{
  title: "Burst Pipe & Water Leak Repair",
  description: "Rapid water shutoff, line location, and trenchless or structural pipe repair to stop flooding fast.",
  badge: "Immediate Dispatch",
  iconName: "Droplets",
  to: "/services/"
}, {
  title: "Water Heater Repair & Replacement",
  description: "Same-day troubleshooting and replacement for tank and tankless water heaters without hot water.",
  badge: "Same-Day Setup",
  iconName: "Flame",
  to: "/water-heater/"
}, {
  title: "Emergency Drain & Sewer Clearing",
  description: "Industrial auger and hydro-jetting equipment to quickly clear severe sewer main backups and blockages.",
  badge: "Heavy Duty",
  iconName: "Wrench",
  to: "/services/"
}];
const problems = [{
  title: "Burst Pipes & Flooding",
  description: "Uncontrolled water pouring from ceiling, wall, or foundation lines.",
  to: "/problems/"
}, {
  title: "Sewer Line Backing Up",
  description: "Raw sewage coming up through floor drains, tubs, or toilets.",
  to: "/problems/"
}, {
  title: "No Hot Water",
  description: "Pilot light out, leaking water heater tank, or failing heating elements.",
  to: "/water-heater/"
}, {
  title: "Overflowing Toilets",
  description: "Obstructions causing bowl overflow that standard plunging cannot resolve.",
  to: "/problems/"
}, {
  title: "Gas Line & Odor Alerts",
  description: "Hissing sounds or rotten egg odors near supply lines and fixtures.",
  to: "/problems/"
}, {
  title: "Low Water Pressure",
  description: "Sudden drops across the entire house pointing to main supply line failures.",
  to: "/problems/"
}];
const serviceAreas = [{
  name: "Jonesboro (Downtown & Historic District)",
  zip: "30236, 30237",
  eta: "15-30 Min"
}, {
  name: "Clayton County / Tara Blvd Corridor",
  zip: "30238",
  eta: "20-35 Min"
}, {
  name: "Morrow & Southlake",
  zip: "30260",
  eta: "20-40 Min"
}, {
  name: "Riverdale & Upper Clayton",
  zip: "30274, 30296",
  eta: "25-45 Min"
}, {
  name: "Lovejoy & Surrounding Subdivisions",
  zip: "30250",
  eta: "20-40 Min"
}, {
  name: "Fayetteville / North Henry Border",
  zip: "30214",
  eta: "30-45 Min"
}];
const faqs = [{
  id: "faq-1",
  question: "How fast can a plumber arrive at my Jonesboro location?",
  answer: "Our technicians operate mobile emergency vans across Jonesboro and Clayton County 24/7. Average response time is between 20 and 45 minutes depending on traffic and exact neighborhood location."
}, {
  id: "faq-2",
  question: "Do you charge extra for late-night, weekend, or holiday calls?",
  answer: "We provide upfront, transparent pricing before any work begins. Our team outlines all costs directly so you know the exact price before we start repairs."
}, {
  id: "faq-3",
  question: "What should I do while waiting for the emergency plumber to arrive?",
  answer: "First, locate and turn off the main water shutoff valve to stop active water flow. If there is electrical equipment near standing water, shut off the corresponding breaker safely. Clear personal items away from the work zone."
}, {
  id: "faq-4",
  question: "Are your technicians licensed and insured in Georgia?",
  answer: "Yes, every technician on our team is fully licensed, insured, and certified under Georgia plumbing codes with equipped service vehicles carrying standard parts."
}, {
  id: "faq-5",
  question: "Can you help document damage for homeowner insurance claims?",
  answer: "Yes, we provide itemized invoices, written assessments of the failure cause, and photographic documentation to support your homeowner insurance filing."
}];
export default function ServicesHub() {
  const shouldReduceMotion = useReducedMotion();
  const getServiceIcon = (name: string) => {
    switch (name) {
      case "Droplets":
        return <Droplets className="size-6 text-primary" />;
      case "Flame":
        return <Flame className="size-6 text-primary" />;
      case "Wrench":
      default:
        return <Wrench className="size-6 text-primary" />;
    }
  };
  return <section id="serviceshubandfaq" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
          <Badge variant="outline" className="mb-3 border-border bg-secondary/50 text-secondary-foreground">
            <Sparkles className="size-3.5 mr-1 text-accent" />
            24/7 Jonesboro Emergency Coverage
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Complete Emergency Plumbing & Local Support
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            From sudden pipe bursts to sewer backups, Jonesboro Emergency Plumber delivers rapid on-site troubleshooting, transparent answers, and immediate repair solutions.
          </p>
        </motion.div>

        {/* Section 1: Related Services Grid */}
        <div className="mb-20">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
            <div>
              <span className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">Core Solutions</span>
              <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground mt-1">
                Emergency Services
              </h3>
            </div>
            <Link to="/services/" className="inline-flex">
              <Button variant="outline" size="sm" className="group">
                View all plumbing services
                <ArrowRight className="size-4 transition-[transform] duration-200 group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {services.map((svc, i) => <div key={svc.title} data-index={i} className="flex">
                <Card className="w-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-200 hover:border-border/80">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="size-12 rounded-lg bg-secondary flex items-center justify-center">
                        {getServiceIcon(svc.iconName)}
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {svc.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-default text-card-foreground">
                      {svc.title}
                    </CardTitle>
                    <CardDescription className="text-sm mt-2 leading-relaxed">
                      {svc.description}
                    </CardDescription>
                  </CardHeader>
                  <CardFooter className="pt-0">
                    <Link to={svc.to} className="w-full">
                      <Button variant="outline" className="w-full justify-between group">
                        <span>Learn details</span>
                        <ArrowRight className="size-4 transition-[transform] duration-200 group-hover:translate-x-1" />
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Section 2: Common Plumbing Problems Grid */}
        <div className="mb-20 rounded-2xl bg-secondary/40 border border-border p-6 sm:p-10">
          <div className="max-w-2xl mb-8">
            <div className="flex items-center gap-2 text-destructive font-semibold text-xs tracking-wider uppercase mb-1">
              <AlertTriangle className="size-4" />
              <span>Urgent Situations</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
              What Problem Are You Facing Right Now?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              Select your immediate issue below for quick mitigation guidance and direct service routing.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {problems.map((prob, i) => <div key={prob.title} data-index={i}>
                <Link to={prob.to} className="block h-full group">
                  <div className="h-full rounded-xl bg-card border border-border p-5 transition-[background-color,border-color,box-shadow] duration-200 group-hover:border-primary/50 group-hover:shadow-sm">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-default font-semibold text-card-foreground group-hover:text-primary transition-colors">
                        {prob.title}
                      </h4>
                      <ArrowRight className="size-4 text-muted-foreground shrink-0 mt-0.5 transition-[transform,color] group-hover:text-primary group-hover:translate-x-0.5" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 leading-normal">
                      {prob.description}
                    </p>
                  </div>
                </Link>
              </div>)}
          </div>
        </div>

        {/* Section 3: Service Areas & Coverage */}
        <div className="mb-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5">
            <Badge variant="outline" className="mb-3">
              <MapPin className="size-3.5 mr-1 text-primary" />
              Clayton County Coverage
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground tracking-tight">
              Rapid Response Service Areas in Jonesboro, GA
            </h3>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
              We position service crews throughout Jonesboro and surrounding South Metro Atlanta communities to keep arrival windows under 45 minutes for urgent plumbing repairs.
            </p>

            <div className="mt-6 space-y-3">
              <div className="flex items-center gap-2.5 text-sm text-foreground">
                <CheckCircle2 className="size-4 text-accent shrink-0" />
                <span>Fully equipped emergency trucks stationed locally</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-foreground">
                <CheckCircle2 className="size-4 text-accent shrink-0" />
                <span>GPS routed for shortest drive times day or night</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-foreground">
                <CheckCircle2 className="size-4 text-accent shrink-0" />
                <span>No delay dispatch on high-risk flooding and gas leaks</span>
              </div>
            </div>

            <div className="mt-8">
              <Link to="/service-areas/">
                <Button variant="secondary" className="group">
                  <span>Explore full service areas map</span>
                  <ArrowRight className="size-4 transition-[transform] duration-200 group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {serviceAreas.map((area, i) => <div key={area.name} data-index={i} className="rounded-xl border border-border bg-card p-4 transition-[border-color,box-shadow] duration-200 hover:border-border/80">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold text-sm text-card-foreground">
                      {area.name}
                    </span>
                    <Badge variant="secondary" className="text-[11px] font-mono shrink-0">
                      <Clock className="size-3 mr-1" />
                      {area.eta}
                    </Badge>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5">
                    <MapPin className="size-3 shrink-0" />
                    <span>ZIP: {area.zip}</span>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        <Separator className="my-16" />

        {/* Section 4: Frequently Asked Questions */}
        <div className="mb-20 max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-2">
              <HelpCircle className="size-3.5 mr-1 text-primary" />
              Common Questions
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
              Frequently Asked Questions
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Clear answers on response timing, estimates, and emergency procedures.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full border border-border rounded-xl px-6 bg-card">
            {faqs.map((faq, i) => <div key={faq.id} data-index={i}>
                <AccordionItem value={faq.id}>
                  <AccordionTrigger className="text-base font-semibold text-card-foreground hover:no-underline hover:text-primary">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </div>)}
          </Accordion>
        </div>

        {/* Section 5: Final CTA */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.4
      }} className="rounded-2xl bg-primary text-primary-foreground p-8 sm:p-12 shadow-lg relative overflow-hidden">
          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8 text-center lg:text-left">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-foreground/10 text-primary-foreground text-xs font-semibold mb-4">
                <ShieldAlert className="size-3.5 text-accent" />
                <span>Immediate 24/7 Emergency Dispatch</span>
              </div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-default font-bold text-primary-foreground tracking-tight">
                Need an Emergency Plumber in Jonesboro Now?
              </h3>
              <p className="mt-3 text-sm sm:text-base text-primary-foreground/90 leading-relaxed">
                Call our direct dispatch line for an immediate response time estimate, or submit a request online. A licensed technician is on standby.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3.5 w-full sm:w-auto shrink-0">
              <a href="tel:4708479076" className="w-full sm:w-auto">
                <Button size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-md">
                  <PhoneCall className="size-4 mr-2" />
                  Call (470) 847-9076
                </Button>
              </a>
              <Link to="/contact/" className="w-full sm:w-auto">
                <Button size="lg" variant="secondary" className="w-full text-secondary-foreground">
                  Request Service Online
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}